import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import {
  useUpdateScene,
  useDeleteScene,
  useCreateScene,
  useGenerateAiContinueScene,
  useGenerateProductLastCut,
  useGenerateLogoLastCut,
  useGenerateTitleCard,
  useAttachBrandCutFromMaterial,
} from '../../../../features/scenarios/hooks';
import {
  starScenario,
  unstarScenario,
  publishScenario,
  unpublishScenario,
  deleteScenario,
  swapSceneOrder,
} from '../../../../features/scenarios/api';
import type { Scenario, ScenarioResponse } from '../types';
import { resolveNarrationText } from '../narrationUtils';
import { shouldDiscardDialogueLinesEdit, type DialogueLine, type SpeakerLine } from '../dialogueLines';
import { shouldDiscardTelopLinesEdit } from '../telopLines';
import { useSceneStateContext } from './SceneStateContext';

type EditingField = {
  sceneId: string;
  field: 'setting' | 'visual' | 'script' | 'narration' | 'dialogue' | 'telop';
} | null;

interface UseSceneActionsParams {
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  activeScenarioId: string | null;
  setActiveScenarioId: (id: string | null) => void;
  scenarioIdFromPath: string | undefined;
  changePhase: (phase: 'setup' | 'work') => void;

  // Asset management
  // 生成フレーム素材だけを選択中素材から外す (ユーザーが選んだ素材は残す)
  removeGeneratedFrameAsset: (id: string) => void;

  // Add-scene modal state
  setAddSceneModalScenarioId: (id: string | null) => void;
  setAddSceneGeneratingLabel: (label: string) => void;

  // Generation settings
  scenarioModel: string;
  autoGenerateEndcardFrame: boolean;
  // storyboard はエンドカードを絵コンテに inline 描画するため、別フレーム生成をスキップする
  videoGenerationMode: string | null;


  // 公開確認モーダルの起動 (採用素材の共有移行を選ばせる共通フロー)。
  // 指定された場合、公開操作はこの確認モーダルに委譲する。
  requestPublish?: (scenarioId: string) => void;
}

export function useSceneActions({
  lastResult,
  setLastResult,
  activeScenarioId,
  setActiveScenarioId,
  scenarioIdFromPath,
  changePhase,
  removeGeneratedFrameAsset,
  setAddSceneModalScenarioId,
  setAddSceneGeneratingLabel,
  scenarioModel,
  autoGenerateEndcardFrame,
  videoGenerationMode,
  requestPublish,
}: UseSceneActionsParams) {
  const {
    scenePreviewMaterials, sceneLastFrames,
    swapKeys: swapSceneKeys, deleteKey: deleteSceneKey,
    setSceneVideoMaterialId,
  } = useSceneStateContext();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Mutations
  const createSceneMutation = useCreateScene();
  const deleteSceneMutation = useDeleteScene();
  const updateSceneMutation = useUpdateScene();
  const aiContinueMutation = useGenerateAiContinueScene();
  const productLastCutMutation = useGenerateProductLastCut();
  const logoLastCutMutation = useGenerateLogoLastCut();
  const titleCardMutation = useGenerateTitleCard();
  const brandCutFromMaterialMutation = useAttachBrandCutFromMaterial();

  // Scene-action-specific state
  const [editingField, setEditingField] = useState<EditingField>(null);
  const [editingValue, setEditingValue] = useState('');
  // handleSaveField の非同期完了時に「まだ同じフィールドを編集中か」を判定するための最新値
  const editingFieldRef = useRef<EditingField>(null);
  editingFieldRef.current = editingField;
  // 構造化セリフ (dialogue_lines) 編集中のローカル state (SceneDetailColumn の
  // editingDialogueLines) を、外側クリックの dirty 判定用にミラーする ref。
  // editingFieldRef と同じ「直近値を ref へ流す」パターンだが、こちらは値の発生源が
  // 別コンポーネント (SceneDetailColumn) 側にあるため、SceneHandlersContext 経由で
  // ref オブジェクトそのものを渡し、書き込みは向こうの useEffect に任せる
  // (2026-08-17 要件2)。
  const editingDialogueLinesRef = useRef<DialogueLine[]>([]);
  // 構造化テロップ (telop_lines) の同型ミラー (行エディタ形 SpeakerLine[])。
  const editingTelopLinesRef = useRef<SpeakerLine[]>([]);
  const [deletingScenes, setDeletingScenes] = useState<Set<string>>(new Set());
  const [addingScenes, setAddingScenes] = useState<Set<string>>(new Set());
  const [swappingScenes, setSwappingScenes] = useState(false);

  // Ref for handleGenerateScenePreview (set after useFrameHandlers initializes)
  const handleGenerateScenePreviewRef = useRef<((scenario: Scenario, scene: Scenario['scenes'][number], options?: { additive?: boolean }) => void) | null>(null);

  // --- Handlers ---

  const handleAddScene = useCallback(async (scenarioId: string, opts?: { afterSceneId?: string }) => {
    const afterSceneId = opts?.afterSceneId;
    try {
      const result = await createSceneMutation.mutateAsync({
        scenarioId,
        payload: {
          setting: '',
          visual: '',
          script: '',
          narration: '',
          dialogue: '',
          // 「シーンを下に追加」: 指定シーンの直後に挿入 (backend が後続を +1 採番)
          ...(afterSceneId ? { after_scene_id: afterSceneId } : {}),
        },
      });

      // NOTE (型): createScene / ai-continue が返すのは scenes テーブルの生行 (order_number 正本) で、
      // 生成 UI の Scenario['scenes'][number] が要求する `order` を持たない。
      // (`order` を付けて返すのはブランドカット系エンドポイントだけ。backend の
      //  scene_generator._fetch_scene / _create_brand_cut_scene 参照)
      // 実行時挙動を変えずに型を揃えられないため、この差し込み経路だけ any 残置とする。
      const newScene: any = result;

      // Update local state
      if (lastResult && result) {
        const targetScenario = lastResult.scenarios.find((s) => s.id === scenarioId);
        const refScene = afterSceneId ? targetScenario?.scenes.find((sc) => sc.id === afterSceneId) : undefined;
        if (targetScenario && refScene) {
          // 途中挿入: backend の再採番に合わせ、後続シーンの order を +1 する。
          // order ベースのシーンkeyで持つセッション状態 (プレビュー/動画/再生済み等) も
          // 降順に 1 つ後ろのkeyへスワップして追随させる (空keyとのスワップ=移動)。
          // NOTE: sceneVideoSettings / extraVideoCardOrders はここではずらさない
          // (既存のシーン並べ替え handleSwapSceneOrder と同じ扱い)。
          const followingOrders = targetScenario.scenes
            .filter((sc) => sc.order > refScene.order)
            .map((sc) => sc.order)
            .sort((a, b) => b - a);
          for (const o of followingOrders) {
            swapSceneKeys(`${scenarioId}-scene-${o}`, `${scenarioId}-scene-${o + 1}`);
          }
          setLastResult((prev) => {
            if (!prev) return prev;
            const updatedScenarios = prev.scenarios.map((s) => {
              if (s.id !== scenarioId) return s;
              // 作成 API の応答を待つ間に別経路の再取得 (動画完了時の getScenario 等) が
              // 先に反映されていると newScene は既に含まれている。二重挿入と後続 order の
              // 二重加算を防ぐため、その場合は何もしない。
              if (s.scenes.some((sc) => sc.id === newScene.id)) return s;
              const shifted = s.scenes.map((sc) =>
                sc.order > refScene.order ? { ...sc, order: sc.order + 1 } : sc,
              );
              const inserted = [...shifted, { ...newScene, order: newScene.order ?? refScene.order + 1 }];
              inserted.sort((a, b) => a.order - b.order);
              return { ...s, scenes: inserted };
            });
            return { ...prev, scenarios: updatedScenarios };
          });
        } else {
          setLastResult((prev) => {
            if (!prev) return prev;
            const updatedScenarios = prev.scenarios.map((s) => {
              if (s.id === scenarioId) {
                // 途中挿入と同じく、再取得で既に含まれていれば二重追加しない
                if (s.scenes.some((sc) => sc.id === newScene.id)) return s;
                return {
                  ...s,
                  scenes: [...s.scenes, newScene],
                };
              }
              return s;
            });
            return { ...prev, scenarios: updatedScenarios };
          });
        }
      }

      // Mark scene as adding (triggers animation)
      if (result?.id) {
        setAddingScenes((prev) => new Set(prev).add(result.id));

        // Remove from adding set after animation completes
        setTimeout(() => {
          setAddingScenes((prev) => {
            const next = new Set(prev);
            next.delete(result.id);
            return next;
          });
        }, 500); // Match CSS animation duration
      }

      toast.success('シーンを追加しました');
    } catch (error) {
      console.error('Failed to create scene:', error);
      toast.error('シーンの追加に失敗しました');
    }
  }, [createSceneMutation, lastResult, setLastResult, swapSceneKeys, toast]);

  // 追加/置換したシーンの動画素材IDをセッション状態に同期する。
  // sceneVideoMaterialId[key] は scene.video_material_id より優先されるため、ここで
  // 新しいシーンの値に揃えておかないと、同じ key (= order) に古い動画素材が紐付いた
  // ままになり、別素材で追加し直しても前の動画が表示されてしまう。
  const syncSceneVideoMaterialId = useCallback((key: string, videoMaterialId?: string | null) => {
    setSceneVideoMaterialId((prev) => {
      const next = { ...prev };
      if (videoMaterialId) {
        next[key] = videoMaterialId;
      } else {
        delete next[key];
      }
      return next;
    });
  }, [setSceneVideoMaterialId]);

  // Helper to update local state with a new scene (shared by AI/product/logo handlers)
  // NOTE (型): scene は API が返す scenes 行。生成 UI の Scenario['scenes'][number] は `order` を
  // 必須にしているが、createScene / ai-continue の戻り値は order_number しか持たない
  // (order を付けるのはブランドカット系のみ)。実行時挙動を変えずに型を揃えられないため、
  // ローカル state へ差し込むこの経路だけ any 残置とする (呼び出し側の props 型は SceneRow で締めてある)。
  const appendSceneToLocalState = useCallback((scenarioId: string, scene: any) => {
    if (scene) {
      setLastResult((prev) => {
        if (!prev) return prev;
        const updatedScenarios = prev.scenarios.map((s) => {
          if (s.id === scenarioId) {
            return { ...s, scenes: [...s.scenes, scene] };
          }
          return s;
        });
        return { ...prev, scenarios: updatedScenarios };
      });

      if (scene.order != null) {
        const key = `${scenarioId}-scene-${scene.order}`;
        syncSceneVideoMaterialId(key, scene.video_material_id);
      }
    }
    if (scene?.id) {
      setAddingScenes((prev) => new Set(prev).add(scene.id));
      setTimeout(() => {
        setAddingScenes((prev) => {
          const next = new Set(prev);
          next.delete(scene.id);
          return next;
        });
      }, 500);
    }
  }, [setLastResult, syncSceneVideoMaterialId]);

  // タイトルカード(冒頭ブランドカット)はシーン先頭に挿入する。
  // ローカル state では既存に同一ポジションがあれば置換、なければ先頭へ追加する。
  // NOTE (型): appendSceneToLocalState と同じ理由で any 残置。
  const prependSceneToLocalState = useCallback((scenarioId: string, scene: any) => {
    if (scene) {
      setLastResult((prev) => {
        if (!prev) return prev;
        const updatedScenarios = prev.scenarios.map((s) => {
          if (s.id === scenarioId) {
            const others = s.scenes.filter((sc: any) => sc.id !== scene.id && sc.brand_cut_position !== 'start');
            return { ...s, scenes: [scene, ...others] };
          }
          return s;
        });
        return { ...prev, scenarios: updatedScenarios };
      });
      if (scene.order != null) {
        const key = `${scenarioId}-scene-${scene.order}`;
        syncSceneVideoMaterialId(key, scene.video_material_id);
      }
    }
    if (scene?.id) {
      setAddingScenes((prev) => new Set(prev).add(scene.id));
      setTimeout(() => {
        setAddingScenes((prev) => {
          const next = new Set(prev);
          next.delete(scene.id);
          return next;
        });
      }, 500);
    }
  }, [setLastResult, syncSceneVideoMaterialId]);

  const handleAiContinueScene = useCallback(async (scenarioId: string) => {
    setAddSceneGeneratingLabel('AIがシーンの続きを生成中...');
    try {
      const result = await aiContinueMutation.mutateAsync({ scenarioId, model: scenarioModel });
      appendSceneToLocalState(scenarioId, result);
      toast.success('AIがシーンの続きを生成しました');
      setAddSceneModalScenarioId(null);
    } catch (error: any) {
      console.error('Failed to generate AI continue scene:', error);
      const message = error?.response?.data?.error || 'AI続き提案の生成に失敗しました';
      toast.error(message);
    } finally {
      setAddSceneGeneratingLabel('');
    }
  }, [aiContinueMutation, scenarioModel, appendSceneToLocalState, toast, setAddSceneModalScenarioId, setAddSceneGeneratingLabel]);

  const handleProductLastCut = useCallback(async (scenarioId: string) => {
    setAddSceneGeneratingLabel('商品カットを生成中...');
    try {
      const result = await productLastCutMutation.mutateAsync({ scenarioId, model: scenarioModel });
      appendSceneToLocalState(scenarioId, result);
      toast.success('商品カットを作成しました');
      setAddSceneModalScenarioId(null);
      // フレーム自動生成 (storyboard は絵コンテに inline 描画するためスキップ)
      if (autoGenerateEndcardFrame && result && videoGenerationMode !== 'storyboard') {
        const scenario = lastResult?.scenarios.find(s => s.id === scenarioId);
        if (scenario) {
          const updatedScenario = { ...scenario, scenes: [...scenario.scenes, result] };
          handleGenerateScenePreviewRef.current?.(updatedScenario, result);
        }
      }
    } catch (error: any) {
      console.error('Failed to generate product last cut:', error);
      const message = error?.response?.data?.error || '商品カットの生成に失敗しました';
      toast.error(message);
    } finally {
      setAddSceneGeneratingLabel('');
    }
  }, [productLastCutMutation, scenarioModel, appendSceneToLocalState, toast, setAddSceneModalScenarioId, setAddSceneGeneratingLabel, autoGenerateEndcardFrame, videoGenerationMode, lastResult]);

  const handleLogoLastCut = useCallback(async (scenarioId: string) => {
    setAddSceneGeneratingLabel('ロゴラストカットを作成中...');
    try {
      const result = await logoLastCutMutation.mutateAsync({ scenarioId, model: scenarioModel });
      appendSceneToLocalState(scenarioId, result);
      toast.success('ロゴラストカットを作成しました');
      setAddSceneModalScenarioId(null);
      // フレーム自動生成 (storyboard は絵コンテに inline 描画するためスキップ)
      if (autoGenerateEndcardFrame && result && videoGenerationMode !== 'storyboard') {
        const scenario = lastResult?.scenarios.find(s => s.id === scenarioId);
        if (scenario) {
          const updatedScenario = { ...scenario, scenes: [...scenario.scenes, result] };
          handleGenerateScenePreviewRef.current?.(updatedScenario, result);
        }
      }
    } catch (error: any) {
      console.error('Failed to generate logo last cut:', error);
      const message = error?.response?.data?.error || 'ロゴラストカットの作成に失敗しました';
      toast.error(message);
    } finally {
      setAddSceneGeneratingLabel('');
    }
  }, [logoLastCutMutation, scenarioModel, appendSceneToLocalState, toast, setAddSceneModalScenarioId, setAddSceneGeneratingLabel, autoGenerateEndcardFrame, videoGenerationMode, lastResult]);

  const handleTitleCard = useCallback(async (scenarioId: string) => {
    setAddSceneGeneratingLabel('タイトルカードを作成中...');
    try {
      const result = await titleCardMutation.mutateAsync({ scenarioId, model: scenarioModel });
      prependSceneToLocalState(scenarioId, result);
      toast.success('タイトルカードを作成しました');
      setAddSceneModalScenarioId(null);
      // フレーム自動生成 (storyboard は絵コンテに inline 描画するためスキップ)
      if (autoGenerateEndcardFrame && result && videoGenerationMode !== 'storyboard') {
        const scenario = lastResult?.scenarios.find(s => s.id === scenarioId);
        if (scenario) {
          const updatedScenario = { ...scenario, scenes: [result, ...scenario.scenes] };
          handleGenerateScenePreviewRef.current?.(updatedScenario, result);
        }
      }
    } catch (error: any) {
      console.error('Failed to generate title card:', error);
      const message = error?.response?.data?.error || 'タイトルカードの作成に失敗しました';
      toast.error(message);
    } finally {
      setAddSceneGeneratingLabel('');
    }
  }, [titleCardMutation, scenarioModel, prependSceneToLocalState, toast, setAddSceneModalScenarioId, setAddSceneGeneratingLabel, autoGenerateEndcardFrame, videoGenerationMode, lastResult]);

  // 編集画面の「継ぎ足し」: 既存素材を紐付けてブランドカット(冒頭/末尾)を追加 (AI生成・課金なし)
  const handleBrandCutFromMaterial = useCallback(async (
    scenarioId: string,
    params: { position: 'start' | 'end'; kind: 'logo' | 'product'; imageUrl: string; materialId?: string },
  ) => {
    try {
      const result = await brandCutFromMaterialMutation.mutateAsync({ scenarioId, ...params });
      if (params.position === 'start') {
        prependSceneToLocalState(scenarioId, result);
      } else {
        appendSceneToLocalState(scenarioId, result);
      }
      toast.success(params.position === 'start' ? 'タイトルカードを追加しました' : 'エンドカードを追加しました');
    } catch (error: any) {
      console.error('Failed to attach brand cut from material:', error);
      const message = error?.response?.data?.error || 'ブランドカットの追加に失敗しました';
      toast.error(message);
    }
  }, [brandCutFromMaterialMutation, prependSceneToLocalState, appendSceneToLocalState, toast]);

  const handleDeleteScene = useCallback((scenarioId: string, sceneId: string) => {
    // Find scene order before deletion for cleanup
    const targetScene = lastResult?.scenarios
      .find((s) => s.id === scenarioId)
      ?.scenes.find((sc) => sc.id === sceneId);
    const sceneKey = targetScene
      ? `${scenarioId}-scene-${targetScene.order}`
      : null;

    // Mark scene as deleting (triggers animation)
    setDeletingScenes((prev) => new Set(prev).add(sceneId));

    // Wait for animation, then delete
    setTimeout(async () => {
      try {
        await deleteSceneMutation.mutateAsync(sceneId);

        // Remove scene materials from selectedAssets
        if (sceneKey) {
          const materialsForScene = scenePreviewMaterials[sceneKey] || [];
          materialsForScene.forEach((m) => {
            if (m?.id) removeGeneratedFrameAsset(m.id);
          });

          // Remove last frame material from selectedAssets
          const lastFrameData = sceneLastFrames[sceneKey];
          const lastFrameMaterialId = lastFrameData?.materialId || targetScene?.last_frame_material_id;
          if (lastFrameMaterialId) {
            removeGeneratedFrameAsset(lastFrameMaterialId);
          }

          // Clean up all scene-keyed states at once
          deleteSceneKey(sceneKey);
        }

        // Update local state
        if (lastResult) {
          setLastResult((prev) => {
            if (!prev) return prev;
            const updatedScenarios = prev.scenarios.map((s) => {
              if (s.id === scenarioId) {
                return {
                  ...s,
                  scenes: s.scenes.filter((sc) => sc.id !== sceneId),
                };
              }
              return s;
            });
            return { ...prev, scenarios: updatedScenarios };
          });
        }

        // Remove from deleting set
        setDeletingScenes((prev) => {
          const next = new Set(prev);
          next.delete(sceneId);
          return next;
        });

        toast.success('シーンを削除しました');
      } catch (error) {
        console.error('Failed to delete scene:', error);
        toast.error('シーンの削除に失敗しました');

        // Remove from deleting set on error
        setDeletingScenes((prev) => {
          const next = new Set(prev);
          next.delete(sceneId);
          return next;
        });
      }
    }, 300); // Match CSS transition duration
  }, [lastResult, deleteSceneMutation, scenePreviewMaterials, sceneLastFrames, removeGeneratedFrameAsset, deleteSceneKey, setLastResult, toast]);

  const handleSwapSceneOrder = useCallback(async (scenarioId: string, sceneIdA: string, sceneIdB: string) => {
    if (swappingScenes) return;
    setSwappingScenes(true);

    // スワップ前にシーンAの画面上の位置を記録
    const sceneElBefore = document.querySelector(`[data-scene-id="${sceneIdA}"]`);
    const topBefore = sceneElBefore ? sceneElBefore.getBoundingClientRect().top : null;

    try {
      await swapSceneOrder(sceneIdA, sceneIdB);

      // Update local state: swap order values
      if (lastResult) {
        // Swap preview states by key (副作用なので updater の外で 1 回だけ行う。
        // StrictMode は updater を二重実行するため、中で呼ぶと元に戻ってしまう)
        const currentScenario = lastResult.scenarios.find((s) => s.id === scenarioId);
        const sceneA = currentScenario?.scenes.find((sc) => sc.id === sceneIdA);
        const sceneB = currentScenario?.scenes.find((sc) => sc.id === sceneIdB);
        if (sceneA && sceneB) {
          const orderA = sceneA.order;
          const orderB = sceneB.order;
          // Swap all scene-keyed states at once
          swapSceneKeys(`${scenarioId}-scene-${orderA}`, `${scenarioId}-scene-${orderB}`);
          // 最新 state に「交換」を再適用するのではなく、確定した移動先の order を絶対値で
          // 設定する。API 応答を待つ間に別経路の再取得 (動画完了時の getScenario 等) が
          // 交換後の順序を先に反映していても、再度入れ替わって元に戻ることがない。
          setLastResult((prev) => {
            if (!prev) return prev;
            const updatedScenarios = prev.scenarios.map((s) => {
              if (s.id !== scenarioId) return s;
              const updatedScenes = s.scenes.map((sc) => {
                if (sc.id === sceneIdA) return { ...sc, order: orderB };
                if (sc.id === sceneIdB) return { ...sc, order: orderA };
                return sc;
              });
              updatedScenes.sort((a, b) => a.order - b.order);
              return { ...s, scenes: updatedScenes };
            });
            return { ...prev, scenarios: updatedScenarios };
          });
        }

        // 移動したシーンが画面上の同じ位置に留まるようスクロール調整
        if (topBefore !== null) {
          requestAnimationFrame(() => {
            const sceneElAfter = document.querySelector(`[data-scene-id="${sceneIdA}"]`);
            if (sceneElAfter) {
              const topAfter = sceneElAfter.getBoundingClientRect().top;
              const diff = topAfter - topBefore;
              if (Math.abs(diff) > 1) {
                window.scrollBy({ top: diff, behavior: 'smooth' });
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Failed to swap scene order:', error);
      toast.error('シーンの順序変更に失敗しました');
    } finally {
      setSwappingScenes(false);
    }
  }, [swappingScenes, lastResult, swapSceneKeys, setLastResult, toast]);

  const handleSaveField = useCallback(async (sceneId: string, field: 'setting' | 'visual' | 'script' | 'narration' | 'dialogue' | 'telop', value: string) => {
    if (!sceneId) {
      toast.error('シーンを先に保存してください');
      return;
    }

    try {
      const updatedScene = await updateSceneMutation.mutateAsync({
        sceneId,
        payload: { [field]: value },
      });

      // Update local state
      if (lastResult) {
        // 構造化テロップ (telop_lines が非 NULL) のシーンへフラット telop を保存すると、BE が
        // 行単位で話者を引き継いだ telop_lines を再構成して返す (docs/features/
        // scenario-telop-speaker-plan.md §3.6)。ローカル state もその結果で同期する。
        const rebuiltTelopLines =
          field === 'telop' && updatedScene && typeof updatedScene === 'object' && (updatedScene as any).id === sceneId
            && (updatedScene as any).telop_lines !== undefined
            ? { telop_lines: (updatedScene as any).telop_lines, telop: (updatedScene as any).telop ?? value }
            : null;
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map((s) => ({
            ...s,
            scenes: s.scenes.map((sc) => {
              if (sc.id === sceneId) {
                return { ...sc, [field]: value, ...(rebuiltTelopLines ?? {}) };
              }
              return sc;
            }),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }

      // ナレーション (実写のナレーター行)・セリフは話者の一覧とナレーションの鮮度 (voice-map) を変える
      if (field === 'narration' || field === 'dialogue' || field === 'script') {
        void queryClient.invalidateQueries({ queryKey: ['scenario-voice-map'] });
      }
      toast.success(`${field === 'setting' ? '場所・設定' : field === 'visual' ? '映像・ビジュアル' : field === 'telop' ? 'テロップ' : 'ナレーション・スクリプト'}を更新しました`);
      // フォーム外クリック→別フィールドの鉛筆クリックが連続すると、この保存の完了前に
      // 次のインライン編集が始まっている。その場合は新しい編集状態を消さない。
      if (editingFieldRef.current?.sceneId === sceneId && editingFieldRef.current?.field === field) {
        setEditingField(null);
        setEditingValue('');
      }
    } catch (error) {
      console.error('Failed to update scene field:', error);
      toast.error('更新に失敗しました');
    }
  }, [updateSceneMutation, lastResult, setLastResult, toast, queryClient]);

  // 鉛筆アイコンからのインライン編集中にフォーム外をクリックしたら、編集内容を適用
  // (保存) して編集モードを閉じる。フォーム内 (textarea / 保存 / キャンセルボタン) は
  // data-scene-inline-edit マーカーで除外する。click ではなく mousedown で判定するため、
  // 別フィールドの鉛筆をクリックした場合も先に現在の編集が適用されてから次が開く。
  useEffect(() => {
    if (!editingField) return;
    const { sceneId, field } = editingField;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('[data-scene-inline-edit]')) return;
      if (!sceneId) {
        // 未保存シーンは適用できないため閉じるだけ
        setEditingField(null);
        setEditingValue('');
        return;
      }
      const scene = lastResult?.scenarios
        .flatMap((s) => s.scenes)
        .find((sc) => sc.id === sceneId);
      // 構造化モード (dialogue_lines が非 NULL) のセリフは行エディタ (DialogueLinesEditor)
      // が別の local state (配列) で編集内容を持ち、editingValue (文字列) は無関係。
      // ここで handleSaveField(field='dialogue') を呼ぶと、BE は構造化モードシーンへの
      // クライアント製フラット dialogue を無視する (無害) が、ローカル state の
      // scene.dialogue だけが editingValue の値で不整合に上書きされてしまうため、
      // 明示的な保存/キャンセルボタンのみで確定させる (外側クリックでは保存しない)。
      // docs/features/scenario-dialogue-speaker-plan.md §3.6/§3.8。
      // ただし無警告での破棄は事故のもとなので、編集開始時の値 (scene.dialogue_lines)
      // から変更があるときだけ確認する (2026-08-17 要件2)。未変更なら従来どおり無言で
      // 閉じる。editingDialogueLinesRef は SceneDetailColumn 側の useEffect が
      // 編集中だけ書き込む「直近値」— 別フィールドの外側クリックではこの分岐へ
      // そもそも入らないため古い値が残っていても無害。
      if (field === 'dialogue' && scene?.dialogue_lines != null) {
        const discard = shouldDiscardDialogueLinesEdit(
          editingDialogueLinesRef.current,
          scene.dialogue_lines,
          (message) => window.confirm(message),
        );
        if (!discard) return; // キャンセル: 編集継続 (閉じない)
        setEditingField(null);
        setEditingValue('');
        return;
      }
      // 構造化テロップ (telop_lines) も同じく明示保存のみ (docs/features/scenario-telop-speaker-plan.md §3.7)。
      if (field === 'telop' && scene?.telop_lines != null) {
        const discard = shouldDiscardTelopLinesEdit(
          editingTelopLinesRef.current,
          scene.telop_lines,
          (message) => window.confirm(message),
        );
        if (!discard) return;
        setEditingField(null);
        setEditingValue('');
        return;
      }
      const currentValue = scene
        ? (field === 'narration' ? resolveNarrationText(scene) : (scene[field] ?? ''))
        : null;
      if (currentValue !== null && currentValue === editingValue) {
        // 変更なし: 保存 API を呼ばず閉じるだけ
        setEditingField(null);
        setEditingValue('');
        return;
      }
      void handleSaveField(sceneId, field, editingValue);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [editingField, editingValue, lastResult, handleSaveField]);

  const handleToggleStar = useCallback(async (scenarioId: string, currentStarred: boolean) => {
    try {
      if (currentStarred) {
        await unstarScenario(scenarioId);
        toast.success('お気に入りから外しました');
      } else {
        await starScenario(scenarioId);
        toast.success('お気に入りに追加しました');
      }

      // Update local state
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s =>
            s.id === scenarioId ? { ...s, starred: !currentStarred } : s
          );
          return { ...prev, scenarios: updatedScenarios };
        });
      }
    } catch (error) {
      console.error('Failed to toggle star:', error);
      toast.error('お気に入りの更新に失敗しました');
    }
  }, [lastResult, setLastResult, toast]);

  const handleTogglePublish = useCallback(async (scenarioId: string, currentPublished: boolean) => {
    // 公開は確認モーダル (採用素材の共有移行を選ばせる) に委譲する。
    // 反映は usePublishConfirm の onPublished 経由で行う。
    if (!currentPublished && requestPublish) {
      requestPublish(scenarioId);
      return;
    }
    try {
      if (currentPublished) {
        await unpublishScenario(scenarioId);
        toast.success('共有を解除しました');
      } else {
        // requestPublish 未指定のフォールバック (確認なし)
        await publishScenario(scenarioId);
        toast.success('共有しました');
      }

      // Update local state
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s =>
            s.id === scenarioId ? { ...s, published: !currentPublished } : s
          );
          return { ...prev, scenarios: updatedScenarios };
        });
      }
    } catch (error) {
      console.error('Failed to toggle publish:', error);
      toast.error('共有状態の更新に失敗しました');
    }
  }, [lastResult, setLastResult, toast, requestPublish]);

  const handleDeleteScenario = useCallback(async (scenarioId: string, _scenarioTitle: string) => {
    try {
      await deleteScenario(scenarioId);
      toast.success('シナリオをごみ箱に移動しました');

      // Remove the deleted scenario from local state
      if (lastResult) {
        const remaining = lastResult.scenarios.filter(s => s.id !== scenarioId);
        if (remaining.length === 0) {
          setLastResult(null);
          setActiveScenarioId(null);
          changePhase('setup');
          navigate('/studio/video', { replace: true });
        } else {
          setLastResult((prev) => (prev ? { ...prev, scenarios: prev.scenarios.filter((s) => s.id !== scenarioId) } : prev));
          // 削除されたシナリオが選択中だった場合、別のシナリオに切り替え
          if (activeScenarioId === scenarioId || scenarioIdFromPath === scenarioId) {
            const nextId = remaining[0].id;
            setActiveScenarioId(nextId);
            navigate(`/studio/video/${nextId}`, { replace: true });
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      toast.error('シナリオの削除に失敗しました');
    }
  }, [lastResult, setLastResult, activeScenarioId, setActiveScenarioId, scenarioIdFromPath, changePhase, navigate, toast]);

  const handleCloseScenarioTab = useCallback((scenarioId: string) => {
    if (!lastResult) return;
    const remaining = lastResult.scenarios.filter(s => s.id !== scenarioId);
    if (remaining.length === 0) {
      setLastResult(null);
      setActiveScenarioId(null);
      changePhase('setup');
      navigate('/studio/video', { replace: true });
    } else {
      setLastResult((prev) => (prev ? { ...prev, scenarios: prev.scenarios.filter((s) => s.id !== scenarioId) } : prev));
      if (activeScenarioId === scenarioId || scenarioIdFromPath === scenarioId) {
        const nextId = remaining[0].id;
        setActiveScenarioId(nextId);
        navigate(`/studio/video/${nextId}`, { replace: true });
      }
    }
  }, [lastResult, setLastResult, activeScenarioId, setActiveScenarioId, scenarioIdFromPath, changePhase, navigate]);

  return {
    // State
    editingField,
    setEditingField,
    editingValue,
    setEditingValue,
    deletingScenes,
    addingScenes,
    swappingScenes,

    // Ref for deferred binding of handleGenerateScenePreview
    handleGenerateScenePreviewRef,
    // 構造化セリフ編集中の最新値ミラー ref (外側クリックの dirty 判定用・要件2)。
    // 書き込みは SceneDetailColumn 側が担う (SceneHandlersContext 経由)。
    editingDialogueLinesRef,
    editingTelopLinesRef,

    // Mutations (exposed for isPending checks or external consumers)
    createSceneMutation,
    deleteSceneMutation,
    updateSceneMutation,
    aiContinueMutation,
    productLastCutMutation,
    logoLastCutMutation,
    titleCardMutation,
    brandCutFromMaterialMutation,

    // Handlers
    handleAddScene,
    appendSceneToLocalState,
    prependSceneToLocalState,
    handleAiContinueScene,
    handleProductLastCut,
    handleLogoLastCut,
    handleTitleCard,
    handleBrandCutFromMaterial,
    handleDeleteScene,
    handleSwapSceneOrder,
    handleSaveField,
    handleToggleStar,
    handleTogglePublish,
    handleDeleteScenario,
    handleCloseScenarioTab,
  };
}
